import React, { ReactNode, useContext } from 'react'
import styled from '@emotion/styled'
import L1Title from '../l1/L1Title'
import { L1SmallIconButton } from '../l1/L1Button'
import { faTimes } from '@fortawesome/pro-regular-svg-icons'
import { keyframes } from '@emotion/react'
import { FusionColor, FusionInterfaceZIndex } from '../../../theme/FusionStyle'
import { Theme, useTheme } from '@emotion/react'
import type * as CSS from 'csstype'

type DialogBackdropProps = {
	show: Boolean,
	position?: CSS.Property.Position
} & { theme: Theme }

const fadeIn = keyframes({
	from: { opacity: 0, visibility: 'hidden' },
	to: { opacity: 1, visibility: 'visible' }
})

const fadeOut = keyframes({
	from: { opacity: 1, visibility: 'visible' },
	to: { opacity: 0, visibility: 'hidden' }
})

const DialogBackdrop = styled.div(({ theme, show, position = 'fixed' }: DialogBackdropProps) => ({
	position: position,
	top: 0,
	left: 0,
	zIndex: 1050,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// visibility: 'hidden',
	// opacity: 0,
	width: '100%',
	height: '100%',
	background: 'rgba(0, 0, 0, 0.5)',
	// animation: `${show ? fadeIn : fadeOut} 0.3s ease-in-out forwards`
}))

type L1DialogSize = 'min' | 'medium' | 'max'

type DialogWrapperProps = {
	size?: L1DialogSize,
	fullScreen?: boolean,
	borderRadius?: CSS.Property.BorderRadius,
	flexDirection?: CSS.Property.FlexDirection,
} & { theme: Theme }

type TitleProps = {
	fontSize?: CSS.Property.FontSize,
	textTransform?: CSS.Property.TextTransform,
	color?: CSS.Property.Color
	padding?: CSS.Property.Padding
}

const DialogWrapper = styled.div(({ theme, size, fullScreen, flexDirection = 'column', borderRadius = theme.borderRadius }: DialogWrapperProps) => {
	let width
	let height
	let maxHeight = '95%'
	switch (size) {
		case 'min':
			width = '30%'
			break
		case 'max':
			width = '70%'
			break
		case 'medium':
		default:
			width = '50%'
	}

	if (fullScreen) {
		width = '100%'
		height = '100%'
		maxHeight = '100%'
	}

	return {
		display: 'flex',
		flexDirection,
		width,
		height: height || 'auto',
		maxHeight,
		borderRadius: borderRadius,
		backgroundColor: theme.colors.white,
		boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.5)',
		outline: 0,
	}
})

type HeaderProps = {
	display?: CSS.Property.Display
	justifyContent?: CSS.Property.JustifyContent
	alignItems?: CSS.Property.AlignItems
	flexDirection?: CSS.Property.FlexDirection
	padding?: CSS.Property.Padding,
	background?: CSS.Property.Background
	showBoxShadow?: boolean
} & {
	theme?: Theme
}

const DialogHeader = styled.div(({ theme, showBoxShadow = false, display = 'flex', justifyContent = 'space-between', alignItems = 'center', flexDirection = undefined, padding = theme?.spacing(2), background = 'transparent' }: HeaderProps) => ({
	display: display,
	alignItems: alignItems,
	justifyContent: justifyContent,
	padding: padding,
	background: background,
	flexDirection: flexDirection,
	boxShadow: showBoxShadow ? '0 4px 4px rgba(0, 0, 0, 0.2)' : undefined,
	zIndex: 1,
}))

type L2DialogContentProps = {
	height?: string
    padding?: CSS.Property.Padding,
	background?: CSS.Property.BackgroundColor
} & { theme: Theme }

export const L2DialogContent = styled.div(({ theme, height, padding = theme?.spacing(2), background = theme.colors.second }: L2DialogContentProps) => {
	const { size } = useContext(DialogContext);

	let maxHeight
	switch (size) {
		case 'min':
			maxHeight = '30vh'
			break
		case 'max':
			maxHeight = '70vh'
			break
		case 'medium':
		default:
			maxHeight = '50vh'
	}
	
	return {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flex: "1 1 auto",
		padding: padding,
		backgroundColor: background,
		maxHeight,
		height: height ?? 'auto',
	}
})

type L2DialogActionsProps = {
	display?: CSS.Property.Display,
	flexDirection?: CSS.Property.FlexDirection,
	alignItems?: CSS.Property.AlignItems,
	justifyContent?: CSS.Property.JustifyContent,
	padding?: CSS.Property.Padding,
	background?: CSS.Property.Background,
	showBoxShadow?: boolean
} & {
	theme: Theme
}

export const L2DialogActions = styled.div(({ 
	theme, 
	display = 'flex',
	alignItems = 'center',
	justifyContent = 'center',
	flexDirection = 'row',
	padding = theme.spacing(2),
	background = 'transparent',
	showBoxShadow = false,
}: L2DialogActionsProps) => ({
	display: display,
	alignItems: alignItems,
	justifyContent: justifyContent,
	flexDirection: flexDirection,
	gap: theme.spacing(1),
	padding: padding,
	background: background,
	zIndex: 9999,
	boxShadow: showBoxShadow ? '0 -4px 4px rgba(0, 0, 0, 0.2)' : undefined
}))

const DialogContext = React.createContext<{ size?: L1DialogSize }>({});

export interface L2DialogProps {
	backdropPosition?: CSS.Property.Position
	borderRadius?: CSS.Property.BorderRadius
	headerChildren?: ReactNode
	headerProps?: HeaderProps
	titleProps?: TitleProps
	show: Boolean
	showCloseButton?: Boolean
	size?: L1DialogSize
	title: string
	children?: ReactNode
	footerContent?: ReactNode
	fullScreen?: boolean
	onClose?: () => void
}

export const L2Dialog = ({ 
	borderRadius, 
	backdropPosition = 'fixed', 
	show,
	showCloseButton = true,
	size, 
	title, 
	children, 
	footerContent, 
	fullScreen, 
	onClose,
	headerChildren,
	headerProps,
	titleProps
}: L2DialogProps) => {
	if (!show) {
		return null
	}

	return (
		<DialogBackdrop show={show} position={backdropPosition}>
			<DialogWrapper size={size} fullScreen={fullScreen} borderRadius={borderRadius}>
				<DialogHeader {...headerProps}>
					<L1Title title={title} titleStyle={titleProps} />
					{onClose && showCloseButton ? <L1SmallIconButton icon={faTimes} color={FusionColor.Second} onClick={onClose} /> : null}
					{headerChildren}
				</DialogHeader>
				<DialogContext.Provider value={{ size }}>
          			{children}
        		</DialogContext.Provider>
			</DialogWrapper>
		</DialogBackdrop>
	)
}
